import { useState, useEffect, useCallback, useRef } from 'react';
import { toastr } from 'react-redux-toastr';

import { mapOrganisationSitesToOptions } from '../utils/siteReassignUtils';

/**
 * Loads organisation site options for reassign UI.
 * Skips setState after unmount (e.g. modal closed before fetch completes).
 */
export function useOrganisationSiteOptions(orgId, getOrganisationSites) {
  const [siteOptions, setSiteOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const getOrganisationSitesRef = useRef(getOrganisationSites);
  getOrganisationSitesRef.current = getOrganisationSites;

  useEffect(() => {
    if (!orgId || !getOrganisationSitesRef.current) {
      return undefined;
    }

    let isActive = true;
    setLoading(true);

    getOrganisationSitesRef.current(orgId)
      .then((sites) => {
        if (!isActive) return;
        setSiteOptions(mapOrganisationSitesToOptions(sites));
      })
      .catch(() => {
        if (!isActive) return;
        setSiteOptions([]);
      })
      .finally(() => {
        if (!isActive) return;
        setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [orgId]);

  return { siteOptions, loading };
}

/**
 * Shared org site-reassign logic for sensor detail views.
 * Pass getTransferDeviceParams to resolve mote vs tx (and other) transfer payloads.
 */
export function useSiteReassign({
  orgId,
  getOrganisationSites,
  transferDeviceToSite,
  t,
  getSiteAccountId,
  getTransferDeviceParams,
  hasMachineAssociation,
  disabledHint,
  onReassignSuccess,
}) {
  const { siteOptions } = useOrganisationSiteOptions(orgId, getOrganisationSites);
  const [reassignTargetSiteId, setReassignTargetSiteId] = useState('');
  const [reassignSubmitting, setReassignSubmitting] = useState(false);

  const currentSiteAccountId = getSiteAccountId ? getSiteAccountId() : '';
  const selectValue = reassignTargetSiteId !== '' ? reassignTargetSiteId : currentSiteAccountId;
  const selectedOption = siteOptions.find(o => String(o.value) === String(currentSiteAccountId));
  const siteDisplayName = (selectedOption && selectedOption.text) || '--';

  const handleReassignToSite = useCallback((targetAccountId) => {
    if (!orgId || !targetAccountId) {
      return Promise.resolve();
    }

    const deviceParams = getTransferDeviceParams ? getTransferDeviceParams() : null;
    if (!deviceParams) {
      toastr.warning(t('sensors.reassignSite.noTransmitterId'));
      return Promise.resolve();
    }

    if (hasMachineAssociation && hasMachineAssociation()) {
      toastr.warning(disabledHint || t('sensors.reassignSite.dissociateFirst'));
      return Promise.resolve();
    }

    const currentSite = getSiteAccountId();
    if (String(targetAccountId) === String(currentSite)) {
      return Promise.resolve();
    }

    setReassignSubmitting(true);
    return transferDeviceToSite({
      orgId,
      ...deviceParams,
      target_account_id: targetAccountId,
    })
      .then(() => {
        setReassignSubmitting(false);
        setReassignTargetSiteId('');
        toastr.success(t('sensors.reassignSite.success'));
        if (onReassignSuccess) onReassignSuccess(targetAccountId);
      })
      .catch(() => setReassignSubmitting(false));
  }, [
    orgId,
    getSiteAccountId,
    getTransferDeviceParams,
    hasMachineAssociation,
    disabledHint,
    transferDeviceToSite,
    t,
    onReassignSuccess,
  ]);

  const handleSiteSelectChange = useCallback((value) => {
    const currentId = getSiteAccountId();
    if (!value || String(value) === String(currentId)) {
      setReassignTargetSiteId('');
      return;
    }
    setReassignTargetSiteId(value);
  }, [getSiteAccountId]);

  const handleCancelPendingSite = useCallback(() => {
    setReassignTargetSiteId('');
  }, []);

  const handleConfirmReassign = useCallback(() => {
    if (!reassignTargetSiteId) return;
    handleReassignToSite(reassignTargetSiteId);
  }, [reassignTargetSiteId, handleReassignToSite]);

  const hasPendingReassign = reassignTargetSiteId !== ''
    && String(reassignTargetSiteId) !== String(currentSiteAccountId);

  return {
    siteOptions,
    reassignSubmitting,
    selectValue,
    siteDisplayName,
    hasPendingReassign,
    handleSiteSelectChange,
    handleCancelPendingSite,
    handleConfirmReassign,
    handleReassignToSite,
  };
}
